import React, { useEffect, useMemo, useState } from "react";
import { db } from "../../firebase/config";
import {
  collection,
  query,
  where,
  onSnapshot,
  doc,
  deleteDoc,
  getDoc,
} from "firebase/firestore";
import { useLocation, useNavigate } from "react-router-dom";
import Chat from "./Chat";
import FarmAvatar from "../Common/FarmAvatar";
import { OFFICIAL_ADMIN_PROFILE } from "./messageIdentity";


  const Inbox = ({ currentUser, userProfile }) => {
  const [conversations, setConversations] = useState([]);
  const [activeConversation, setActiveConversation] = useState(null);
  const [search, setSearch] = useState("");
  const location = useLocation();
  const navigate = useNavigate();
  const [showProfilePopup, setShowProfilePopup] = useState(false);
  const [liveUsersMap, setLiveUsersMap] = useState({});
  const [inboxFilter, setInboxFilter] = useState("all");
  const [mobileChatOpen, setMobileChatOpen] = useState(false);

  useEffect(() => {
    if (!currentUser?.uid) return;

    const q = query(
      collection(db, "conversations"),
      where("users", "array-contains", currentUser.uid)
    );

    const unsubscribe = onSnapshot(q, (snapshot) => {
      const convos = snapshot.docs.map((docSnap) => ({
        id: docSnap.id,
        ...docSnap.data(),
      }));

      convos.sort(
        (a, b) =>
          (b.lastMessageAt?.seconds || 0) - (a.lastMessageAt?.seconds || 0)
      );

      setConversations(convos);

      const requestedId = location.state?.conversationId;

      if (requestedId) {
        const found = convos.find((c) => c.id === requestedId);
        if (found) setActiveConversation(found);
      } else if (!activeConversation && convos.length > 0) {
        setActiveConversation(convos[0]);
      }
    });

    return () => unsubscribe();
  }, [currentUser?.uid, location.state?.conversationId]);

  

useEffect(() => {
  const loadLiveUsers = async () => {
    if (!currentUser?.uid || conversations.length === 0) return;

    const ids = conversations
      .map((conversation) =>
        conversation.users?.find((id) => id !== currentUser.uid)
      )
      .filter(Boolean);

    const uniqueIds = [...new Set(ids)];

    const userData = {};

    for (const uid of uniqueIds) {
      const snap = await getDoc(doc(db, "users", uid));

      if (snap.exists()) {
        userData[uid] = snap.data();
      }
    }

    setLiveUsersMap(userData);
  };

  loadLiveUsers();
}, [conversations, currentUser?.uid]);

  const isOfficialAdminConversation = (conversation, otherParticipant = {}) => (
    conversation?.isAdminConversation === true ||
    conversation?.adminMessageThread === true ||
    conversation?.official === true ||
    otherParticipant?.type === "admin" ||
    otherParticipant?.role === "admin" ||
    otherParticipant?.official === true
  );

  const getOtherUser = (conversation) => {
  const otherUserId = conversation.users?.find(
    (id) => id !== currentUser.uid
  );

  const oldInfo = conversation.participantsInfo?.[otherUserId] || {};
  const liveInfo = liveUsersMap[otherUserId] || {};

  if (isOfficialAdminConversation(conversation, oldInfo) || isOfficialAdminConversation(conversation, liveInfo)) {
    return {
      ...OFFICIAL_ADMIN_PROFILE,
      id: otherUserId,
      email: "admin@myfarmingpal.com",
      phone: "",
      isOnline: false,
    };
  }

  return {
    id: otherUserId,
    name: liveInfo.name || oldInfo.name || "User",
    email: liveInfo.email || oldInfo.email || "",
    photo: liveInfo.profilePicture || oldInfo.photo || "",
    farmAnimal: liveInfo.farmAnimal || oldInfo.farmAnimal || "",
    farmAnimalSelectionExplicit:
      liveInfo.farmAnimalSelectionExplicit === true ||
      oldInfo.farmAnimalSelectionExplicit === true,
    type: liveInfo.type || oldInfo.type || "Community member",
    role: liveInfo.role || oldInfo.role || "",
    official: liveInfo.official === true || oldInfo.official === true,
    phone: liveInfo.phone || oldInfo.phone || "",
    isOnline: isUserOnline(liveInfo),
  };
};

const isUserOnline = (userData) => {
  if (!userData?.lastActive?.seconds) return false;

  const lastActiveMs = userData.lastActive.seconds * 1000;
  const fiveMinutesMs = 5 * 60 * 1000;

  return Date.now() - lastActiveMs < fiveMinutesMs;
};

const getConversationType = (conversation) => {
  const snapshot = conversation.jobSnapshot || {};
  const title = `${snapshot.title || snapshot.grassType || ""}`.toLowerCase();

  if (
    snapshot.type === "event" ||
    snapshot.jobType === "event" ||
    title.includes("event") ||
    title.includes("reservation")
  ) {
    return "event";
  }

  if (
    snapshot.type === "field" ||
    snapshot.type === "dayJob" ||
    snapshot.jobType === "field" ||
    snapshot.jobId
  ) {
    return "job";
  }

  return "general";
};
  

const filteredConversations = useMemo(() => {
  return conversations.filter((conversation) => {
    const otherUser = getOtherUser(conversation);
    const unread = conversation.unreadCount?.[currentUser.uid] || 0;
    const conversationType = getConversationType(conversation);

    if (inboxFilter === "unread" && unread <= 0) return false;
    if (inboxFilter === "jobs" && conversationType !== "job") return false;
    if (inboxFilter === "events" && conversationType !== "event") return false;

    const name = otherUser?.name || "User";
    const lastMessage = conversation.lastMessage || "";

    return `${name} ${lastMessage}`
      .toLowerCase()
      .includes(search.toLowerCase());
  });
}, [conversations, search, liveUsersMap, inboxFilter, currentUser.uid]);

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this conversation?")) return;

    await deleteDoc(doc(db, "conversations", id));

    if (activeConversation?.id === id) {
      setActiveConversation(null);
    }
  };

  if (!currentUser?.uid) return <p>Loading...</p>;

  const activeOtherUser = activeConversation
    ? getOtherUser(activeConversation)
    : null;
  const activeIsOfficialAdminConversation =
    isOfficialAdminConversation(activeConversation, activeOtherUser);
  const headerName = activeIsOfficialAdminConversation
    ? "MyFarmingPal Admin"
    : activeOtherUser?.name || "User";
  const headerSubtitle = activeIsOfficialAdminConversation
    ? "Official MyFarmingPal message"
    : activeOtherUser?.isOnline
    ? "Online"
    : "Offline";

  return (
    <div className={`mfp-inbox-page ${mobileChatOpen ? "chat-open" : ""}`}>
      <aside className="mfp-inbox-sidebar">
        <div className="mfp-inbox-sidebar-header">
          <div>
            <h2>Messages</h2>
            <p>Your farm conversations</p>
          </div>
          <div className="mfp-inbox-icon">💬</div>
        </div>

        {showProfilePopup && activeOtherUser && (
  <div
    className="mini-profile-overlay"
    onClick={() => setShowProfilePopup(false)}
  >
    <div
      className="mini-profile-card"
      onClick={(e) => e.stopPropagation()}
    >
      <button
        className="mini-profile-close"
        type="button"
        onClick={() => setShowProfilePopup(false)}
      >
        ✕
      </button>

      <div className="mini-profile-header">
        <FarmAvatar
          profile={activeOtherUser}
          className="mini-profile-avatar"
        />

        <div>
          <h3>{activeOtherUser?.name || "User"}</h3>
          <p>{activeOtherUser?.type || "Community member"}</p>
        </div>
      </div>

      <div className="mini-profile-details">
        {activeOtherUser?.email && (
          <p>
            <strong>Email:</strong> {activeOtherUser.email}
          </p>
        )}

        {activeOtherUser?.phone && (
          <p>
            <strong>Phone:</strong> {activeOtherUser.phone}
          </p>
        )}

        <p>
          <strong>Status:</strong> {activeOtherUser?.isOnline ? "Online" : "Offline"}
        </p>
      </div>

      <div className="mini-profile-actions">
        {activeOtherUser?.phone && (
          <button
  type="button"
  onClick={() => {
    if (activeOtherUser?.phone) {
      window.location.href = `tel:${activeOtherUser.phone}`;
    } else {
      alert("This user has not added a phone number.");
    }
  }}
>
  📞
</button>
        )}

        <button
          type="button"
          className="btn-soft"
          onClick={() => setShowProfilePopup(false)}
        >
          Keep Chatting
        </button>
      </div>
    </div>
  </div>
)}

        <div className="mfp-inbox-search">
          <span>🔍</span>
          <label className="sr-only" htmlFor="conversation-search">
  Search conversations
</label>
          <input
             id="conversation-search"
             name="conversationSearch"
             type="search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search conversations..."
          />
        </div>

        <div className="mfp-inbox-tabs">
  <button
    type="button"
    className={inboxFilter === "all" ? "active" : ""}
    onClick={() => setInboxFilter("all")}
  >
    All
  </button>

  <button
    type="button"
    className={inboxFilter === "unread" ? "active" : ""}
    onClick={() => setInboxFilter("unread")}
  >
    Unread
  </button>

  <button
    type="button"
    className={inboxFilter === "jobs" ? "active" : ""}
    onClick={() => setInboxFilter("jobs")}
  >
    Jobs
  </button>

    <button
    type="button"
    className={inboxFilter === "events" ? "active" : ""}
    onClick={() => setInboxFilter("events")}
  >
    Events
  </button>
  
</div>

        <div className="mfp-conversation-list">
          {filteredConversations.length === 0 ? (
            <div className="mfp-empty-inbox">
              <strong>No conversations yet</strong>
              <p>When you message someone, it’ll show up here.</p>
              <div className="empty-state-actions">
                <button
                  type="button"
                  className="btn-primary"
                  onClick={() => navigate("/home")}
                >
                  Browse Jobs
                </button>
                <button
                  type="button"
                  className="btn-soft"
                  onClick={() =>
                    navigate("/home", { state: { dashboardView: "market" } })
                  }
                >
                  Browse Market
                </button>
              </div>
            </div>
          ) : (
            filteredConversations.map((conversation) => {
              const otherUser = getOtherUser(conversation);
              const unread = conversation.unreadCount?.[currentUser.uid] || 0;
              const isMe = conversation.lastSenderId === currentUser.uid;
              const isActive = activeConversation?.id === conversation.id;

              return (
                <div
                  key={conversation.id}
                  className={`mfp-conversation-card ${
                    isActive ? "active" : ""
                  }`}
                  onClick={() => {
                        setActiveConversation(conversation);
                        setMobileChatOpen(true);
                  }}
                >
                  <div className="mfp-avatar-wrap">
                    <FarmAvatar
                      profile={otherUser}
                      className="mfp-inbox-avatar"
                    />
                    <span
  className={`mfp-online-dot ${
    otherUser.isOnline ? "online" : "offline"
  }`}
/>
                  </div>

                  <div className="mfp-conversation-main">
                    <div className="mfp-conversation-top">
                      <strong>{otherUser?.name || "User"}</strong>
                      <span>
                        {conversation.lastMessageAt?.seconds
                          ? new Date(
                              conversation.lastMessageAt.seconds * 1000
                            ).toLocaleTimeString([], {
                              hour: "2-digit",
                              minute: "2-digit",
                            })
                          : ""}
                      </span>
                    </div>

                    <div className="mfp-conversation-bottom">
                      <p>
                        {isMe ? "You: " : ""}
                        {conversation.lastMessage || "Start chatting"}
                      </p>

                      {unread > 0 && (
                        <span className="mfp-unread-badge">{unread}</span>
                      )}
                    </div>
                  </div>

                  <button
                    className="mfp-delete-convo"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleDelete(conversation.id);
                    }}
                    title="Delete conversation"
                  >
                    🗑
                  </button>
                </div>
              );
            })
          )}
        </div>

        <div className="mfp-farm-tip">
          <strong>Farm Tip 🌱</strong>
          <p>Clear communication builds better local farm connections.</p>
        </div>
      </aside>

      <main className="mfp-chat-panel">
        {activeConversation ? (
          <>
            <div className="mfp-chat-header">
  <button
    type="button"
    className="mfp-mobile-back"
    onClick={() => setMobileChatOpen(false)}
  >
    ← Messages
  </button>

  <div className="mfp-chat-user">
                {activeIsOfficialAdminConversation ? (
                  <div className="chat-admin-avatar" aria-hidden="true">
                    🌾
                  </div>
                ) : (
                  <FarmAvatar
                    profile={activeOtherUser}
                    className="mfp-chat-header-avatar"
                  />
                )}

                <div className="chat-header-contact">
                  <h3 className="chat-header-name">{headerName}</h3>
                  <p className="chat-header-status">{headerSubtitle}</p>
                </div>
              </div>

              {!activeIsOfficialAdminConversation && (
              <div className="mfp-chat-actions chat-header-actions">
                <button
  onClick={() => {
    if (activeOtherUser?.phone) {
      window.location.href = `tel:${activeOtherUser.phone}`;
    } else {
      alert("User has no phone number listed");
    }
  }}
>
  📞
</button>
<button type="button" onClick={() => setShowProfilePopup(true)}>
  ℹ️
</button>
              </div>
              )}
            </div>

            <Chat
              conversationId={activeConversation.id}
              currentUser={currentUser}
              userProfile={userProfile}
            />
          </>
        ) : (
          <div className="mfp-no-chat">
            <h3>Select a conversation</h3>
            <p>Pick someone from the left to start chatting.</p>
          </div>
        )}
      </main>
    </div>
  );
};

export default Inbox;
